import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import type { ThemeV2 } from "../../util/types";
import type { QuestionArrangement } from "../contracts";
import type { VideoDefinition } from "./core";

type DialPosition = NonNullable<VideoDefinition["dial"]>["position"];
type SliderOrientation = "horizontal" | "vertical";

export const resolveDialOrientation = (
  position: DialPosition,
): SliderOrientation => (position === "right" ? "vertical" : "horizontal");

export const videoMediaStyle = (
  theme: ThemeV2,
  fullscreen: boolean,
  position: DialPosition | undefined,
  arrangement: QuestionArrangement,
): SystemStyleObject<Theme> => ({
  display: "flex",
  flexDirection:
    position === "right" && arrangement === "wide" ? "row" : "column",
  justifyContent: "center",
  alignItems: "center",
  gap: `${theme.tokens.spacing["4"]}px`,
  width: "100%",
  maxWidth: 940,
  padding: `${theme.tokens.spacing["4"]}px`,
  ...(fullscreen && {
    height: "100%",
    maxWidth: "none",
    overflow: "hidden",
    padding: position
      ? {
          xs: `${theme.tokens.spacing["1"]}px`,
          sm: `${theme.tokens.spacing["4"]}px`,
        }
      : 0,
    border: 0,
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "#000",
  }),
});

export const videoPlayerWrapperStyle = (
  fullscreen: boolean,
  position: DialPosition | undefined,
  arrangement: QuestionArrangement,
): SystemStyleObject<Theme> => {
  if (!fullscreen) {
    return { width: "100%", maxWidth: 900, minWidth: 0 };
  }
  if (!position) {
    return {
      width: "100%",
      height: "100%",
      minWidth: 0,
      minHeight: 0,
      flex: 1,
    };
  }
  if (position === "right" && arrangement === "wide") {
    return {
      width: "70%",
      height: "80%",
      minWidth: 0,
      minHeight: 0,
      flex: "1 1 70%",
    };
  }
  if (arrangement === "compact") {
    return {
      width: "100%",
      height: { xs: 215, sm: "45%" },
      minWidth: 0,
      flex: "0 0 auto",
    };
  }
  return {
    width: "min(100%, 1240px)",
    height: "calc(100% - 9rem)",
    minWidth: 0,
    minHeight: 0,
    flex: "1 1 auto",
  };
};

export const videoPlayerChildrenStyle = (
  fullscreen: boolean,
): SystemStyleObject<Theme> =>
  fullscreen
    ? {
        "& > section, & > iframe": {
          width: "100%",
          height: "100%",
        },
      }
    : {};

export const dialControlsStyle = (
  position: DialPosition,
  arrangement: QuestionArrangement,
): SystemStyleObject<Theme> => {
  if (position === "right" && arrangement === "wide") {
    return {
      width: "30%",
      height: "80%",
      minWidth: 160,
      display: "flex",
      flex: "0 1 30%",
      flexDirection: "column",
      alignItems: "center",
    };
  }
  if (position === "right") {
    return {
      width: "100%",
      height: "40%",
      minHeight: 220,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    };
  }
  return {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };
};

export const dialCaptionStyle = (theme: ThemeV2): SystemStyleObject<Theme> => ({
  color: theme.tokens.color.action.primary,
  fontSize: {
    xs: theme.tokens.typography.size.sm,
    md: theme.tokens.typography.size.md,
  },
  textAlign: "center",
});

export const dialSliderWrapperStyle = (
  orientation: SliderOrientation,
): SystemStyleObject<Theme> => ({
  width: "100%",
  minHeight: 0,
  flex: orientation === "vertical" ? 1 : undefined,
  display: "flex",
  justifyContent: "center",
  alignItems: orientation === "vertical" ? "stretch" : "flex-end",
  paddingBlock: 2,
  paddingInline: orientation === "horizontal" ? 1 : 0,
  marginTop: 4,
});

export const dialSliderStyle = (
  theme: ThemeV2,
  orientation: SliderOrientation,
  pointCount: number,
): SystemStyleObject<Theme> => {
  const primary = theme.tokens.color.action.primary;
  return {
    color: primary,
    width: orientation === "horizontal" ? "100%" : undefined,
    height: orientation === "vertical" ? "100%" : undefined,
    "& .MuiSlider-rail, & .MuiSlider-track": {
      color: primary,
      opacity: 1,
      border: 0,
    },
    "& .MuiSlider-valueLabel": {
      lineHeight: 1.2,
      fontSize: 12,
      width: 32,
      height: 32,
      padding: 0,
      borderRadius: "50% 50% 50% 0",
      backgroundColor: primary,
      color: primary,
      transformOrigin: "bottom left",
      transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
      "&::before": { display: "none" },
      "&.MuiSlider-valueLabelOpen": {
        transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
      },
      "& > *": { transform: "rotate(45deg)" },
    },
    "& .MuiSlider-mark": {
      width: 10,
      height: 10,
      border: `1px solid ${primary}`,
      borderRadius: `${theme.tokens.shape.radius.pill}px`,
      backgroundColor: primary,
      opacity: 1,
    },
    "& .MuiSlider-markLabel": {
      width: pointCount > 6 ? 100 : 200,
      maxWidth: 200,
      color: primary,
      whiteSpace: "normal",
      fontSize: {
        xs: theme.tokens.typography.size.sm,
        md: theme.tokens.typography.size.md,
      },
      textAlign: "center",
      ...(orientation === "vertical"
        ? {
            left: "auto",
            right: "calc(100% + 12px)",
            transform: "translateY(50%)",
            textAlign: "right",
          }
        : {
            top: "calc(100% + 8px)",
          }),
    },
    ...(orientation === "horizontal" && {
      '& .MuiSlider-markLabel[data-index="0"]': {
        transform: "translateX(0)",
        textAlign: "left",
      },
      [`& .MuiSlider-markLabel[data-index="${pointCount - 1}"]`]: {
        transform: "translateX(-100%)",
        textAlign: "right",
      },
    }),
    "& .MuiSlider-thumb": {
      boxSizing: "border-box",
      paddingLeft: 0.8,
      border: 0,
      background: "none",
      boxShadow: "none",
      ...(orientation === "vertical" && {
        transform: "translateX(250%)",
        rotate: "90deg",
      }),
    },
    "& .MuiSlider-thumb.Mui-focusVisible, & .MuiSlider-thumb:hover": {
      boxShadow: "none",
    },
  };
};
